import { useEffect, useState } from 'react';

// Components
import RoundHeader from '../../components/RoundHeader/RoundHeader';
import MatchList from '../../components/MatchList/MatchList';
import TailwindTheme from '../../../../../packages/tailwind/tailwind-theme';
import { ConfigContext, TextBlockTailwind } from '@velinsi/smp-widgets-ui';

// Hooks
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import useRoundMatches from '../../hooks/round/useRoundMatches';
import useTournamentStages from '../../hooks/round/useTournamentStages';

// Types
import { BaseWidgetProps, IRoundConfig } from '../../../../../packages/types';
import { IThemeCustomization, Themes } from '../../../../../packages/tailwind/types';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import tailwindStyles from '../../styles.css';

export const RoundWidget = ({ themes, config, shadow, sdkOptions, cuid, subscribers }: BaseWidgetProps<IRoundConfig>) => {
  const [stageId, setStageId] = useState<string | undefined>(config?.dataStage);
  const [roundId, setRoundId] = useState<string | undefined>(config?.dataRound);

  const { ref } = useRootNodeRef();

  const { tournament, activeStage, activeRound, rounds } = useTournamentStages({
    tournamentId: config?.dataCompetition,
    seasonId: config?.dataSeason,
    sdkOptions,
    dataEntityLinks: config?.dataEntityLinks,
    stageId: stageId,
  });

  const {
    handleClick,
    widgetData: { matches, isLoading: matchesLoading },
  } = useRoundMatches({
    ...config,
    subscribers: subscribers,
    sdkOptions,
    dataWidgetCuid: cuid,
    tournament: config?.dataCompetition,
    season: config?.dataSeason,
    stage: stageId,
    round: roundId,
    dataOddsMarket: config?.dataOddsMarket,
    dataSortDirection: config?.dataSortDirection,
    dataDateFrom: config?.dataDateFrom,
    dataDateTo: config?.dataDateTo,
  });

  useEffect(() => {
    if (!stageId && activeStage?.id) {
      setStageId(activeStage.id);
    }
  }, [activeStage?.id, stageId]);

  useEffect(() => {
    if (!roundId && activeRound) {
      setRoundId(activeRound);
    }
  }, [activeRound, roundId]);

  const currentStage = rounds.find(({ stage }) => stage?.id === stageId)?.stage;
  const currentRound = rounds.find(({ stage }) => stage?.id === currentStage?.id)?.rounds?.find(({ value }) => value === roundId);
  const hasData = Boolean(activeStage && activeRound);

  const handleRoundChange = (value: string) => {
    if (value === config?.dataRound) {
      setRoundId(value);
      return;
    }
    setRoundId(value);
  };

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <TailwindTheme
        isLoading={matchesLoading}
        shadow={shadow}
        themes={themes ? (themes as IThemeCustomization) : undefined}
        dataTheme={config?.dataTheme ? (config.dataTheme as Themes) : undefined}
        refForShadow={ref}
        tailwindStyles={tailwindStyles}
        bgColor='var(--colors-primaryBackgroundColor)'
      >
        <RoundHeader
          isDynamic={config?.dataDropdownDisplay ?? true}
          showHeader={config?.dataHeaderDisplay ?? true}
          tournament={tournament}
          stage={currentStage}
          round={currentRound}
          rounds={rounds}
          setRound={handleRoundChange}
          setStage={setStageId}
          handleClick={handleClick}
        />
        {hasData ? <MatchList matches={matches} handleClick={handleClick} /> : <TextBlockTailwind text={config?.dataLabels?.no_games} />}
      </TailwindTheme>
    </ConfigContext.Provider>
  );
};
